import React, { useEffect, useMemo, useState } from 'react'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from './ui/pagination'

const alignClass = (align?: 'start' | 'center' | 'end'): string => {
  if (align === 'center') return 'text-center'
  if (align === 'end') return 'text-end'
  return 'text-start'
}

const PAGE_NEIGHBOURS = 1

const buildPageRange = (currentPage: number, pageCount: number): Array<number | 'ellipsis-start' | 'ellipsis-end'> => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, idx) => idx + 1)
  }

  const pages: Array<number | 'ellipsis-start' | 'ellipsis-end'> = [1]
  const rangeStart = Math.max(2, currentPage - PAGE_NEIGHBOURS)
  const rangeEnd = Math.min(pageCount - 1, currentPage + PAGE_NEIGHBOURS)

  if (rangeStart > 2) pages.push('ellipsis-start')
  for (let page = rangeStart; page <= rangeEnd; page++) pages.push(page)
  if (rangeEnd < pageCount - 1) pages.push('ellipsis-end')
  pages.push(pageCount)

  return pages
}

export type TableColumn<T = unknown> = {
  key: string
  header: string | React.ReactNode
  render?: (item: T, index: number) => React.ReactNode
  align?: 'start' | 'center' | 'end'
  className?: string
  headerClassName?: string
}

export type SimpleTableProps<T = unknown> = {
  columns: TableColumn<T>[]
  data: T[]
  keyExtractor?: (item: T, index: number) => string | number
  className?: string
  headerClassName?: string
  rowClassName?: string | ((item: T, index: number) => string)
  emptyMessage?: string
  containerClassName?: string
  /** When set, enables client-side pagination with this many rows per page. */
  pageSize?: number
  /** Controlled current page (1-based). When omitted, the component manages its own state. */
  page?: number
  /** Initial page (1-based) for uncontrolled mode. Defaults to 1. */
  defaultPage?: number
  /** Called whenever the active page changes (controlled or uncontrolled). */
  onPageChange?: (page: number) => void
  /** Hides the page-range summary (e.g. "1–10 / 42") even when pagination is active. */
  hidePageSummary?: boolean
  /** When true, all columns get the same width (table-layout: fixed). */
  equalColumns?: boolean
  /** Called when a data row is clicked. */
  onRowClick?: (item: T, index: number) => void
}
export function SimpleTable<T = unknown>({
  columns,
  data,
  keyExtractor,
  className,
  headerClassName,
  rowClassName,
  emptyMessage = 'No data available',
  containerClassName,
  pageSize,
  page: controlledPage,
  defaultPage = 1,
  onPageChange,
  hidePageSummary = false,
  equalColumns = false,
  onRowClick,
}: SimpleTableProps<T>) {
  const [internalPage, setInternalPage] = useState(defaultPage)
  const isPaginated = typeof pageSize === 'number' && pageSize > 0
  const isControlled = controlledPage !== undefined
  const currentPage = controlledPage ?? internalPage

  const pageCount = useMemo(() => {
    if (!isPaginated) return 1
    return Math.max(1, Math.ceil(data.length / pageSize))
  }, [data.length, pageSize, isPaginated])

  // Clamp the active page when data shrinks underneath us.
  useEffect(() => {
    if (!isControlled && currentPage > pageCount) {
      setInternalPage(pageCount)
    }
  }, [isControlled, currentPage, pageCount])

  const visibleData = useMemo(() => {
    if (!isPaginated) return data
    const start = (currentPage - 1) * pageSize
    return data.slice(start, start + pageSize)
  }, [data, isPaginated, currentPage, pageSize])

  const goToPage = (next: number) => {
    const clamped = Math.min(Math.max(1, next), pageCount)
    if (clamped === currentPage) return
    if (!isControlled) setInternalPage(clamped)
    onPageChange?.(clamped)
  }

  const getRowKey = (item: T, index: number): string | number => {
    if (keyExtractor) return keyExtractor(item, index)
    if (typeof item === 'object' && item !== null && 'id' in item) {
      return (item as { id: string | number }).id
    }
    return index
  }

  const getRowClassName = (item: T, index: number): string => {
    if (typeof rowClassName === 'function') return rowClassName(item, index)
    return rowClassName || ''
  }

  if (data.length === 0) {
    return (
      <div
        className={cn(
          'border-border-default text-text-secondary flex items-center justify-center rounded-lg border bg-white py-8 text-sm',
          className
        )}
      >
        {emptyMessage}
      </div>
    )
  }

  const rangeStart = isPaginated ? (currentPage - 1) * pageSize + 1 : 0
  const rangeEnd = isPaginated ? Math.min(currentPage * pageSize, data.length) : data.length
  const showPagination = isPaginated && pageCount > 1
  const pageRange = showPagination ? buildPageRange(currentPage, pageCount) : []

  const tableMinWidthStyle = equalColumns ? { minWidth: `min(100%, ${columns.length * 7}rem)` } : undefined

  return (
    <div className={cn('flex w-full max-w-full min-w-0 flex-col gap-3', className)}>
      <div
        className={cn(
          'border-border-default max-w-full min-w-0 overflow-hidden rounded-lg border bg-white px-2 sm:px-3',
          containerClassName
        )}
      >
        <Table className={cn(equalColumns ? 'w-full table-fixed' : 'w-max min-w-full')} style={tableMinWidthStyle}>
          <TableHeader>
            <TableRow className={cn('border-border-default hover:bg-transparent', headerClassName)}>
              {columns.map(column => (
                <TableHead
                  key={column.key}
                  className={cn(
                    'text-text-secondary h-8 px-1 py-0.5 text-sm font-medium max-sm:px-0.5',
                    alignClass(column.align),
                    column.headerClassName ?? column.className
                  )}
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleData.map((item, index) => (
              <TableRow
                key={getRowKey(item, index)}
                onClick={onRowClick ? () => onRowClick(item, index) : undefined}
                className={cn(
                  'border-border-default hover:bg-background-hover',
                  onRowClick && 'cursor-pointer',
                  getRowClassName(item, index)
                )}
              >
                {columns.map(column => (
                  <TableCell
                    key={column.key}
                    className={cn(
                      'text-text-secondary h-8 px-1 py-0.5 text-sm max-sm:px-0.5',
                      equalColumns && 'overflow-hidden text-ellipsis',
                      alignClass(column.align),
                      column.className
                    )}
                  >
                    {column.render ? column.render(item, index) : String((item as Record<string, unknown>)[column.key] ?? '')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {showPagination && (
        <div dir='ltr' className='flex flex-col items-center justify-between gap-2 sm:flex-row'>
          {!hidePageSummary && (
            <p className='text-text-secondary text-sm'>
              {rangeStart}–{rangeEnd} / {data.length}
            </p>
          )}
          <Pagination className={hidePageSummary ? 'mx-auto' : 'mx-0 w-auto justify-end'}>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => goToPage(currentPage - 1)}
                  isActive={currentPage === 1}
                  aria-disabled={currentPage === 1}
                />
              </PaginationItem>
              {pageRange.map(entry => {
                if (entry === 'ellipsis-start' || entry === 'ellipsis-end') {
                  return (
                    <PaginationItem key={entry}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )
                }
                return (
                  <PaginationItem key={entry}>
                    <PaginationLink size='sm' className='px-2.5' isActive={entry === currentPage} onClick={() => goToPage(entry)}>
                      {entry}
                    </PaginationLink>
                  </PaginationItem>
                )
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => goToPage(currentPage + 1)}
                  isActive={currentPage === pageCount}
                  aria-disabled={currentPage === pageCount}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  )
}
